import { useMemo } from 'react';
import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';

import { useApi } from 'app/shared/contexts/api';
import { ApiClient, ApiClientRequestOptions } from './api-client.types';

const composeUrl = (baseUrl: string, path: string): string => {
  if (!path) return baseUrl;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (!baseUrl) return path;
  const trimmedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const trimmedPath = path.startsWith('/') ? path : `/${path}`;
  return `${trimmedBase}${trimmedPath}`;
};

/**
 * Typed thin wrapper around `@hqo/shared-modules/dist/api-request` that
 * pre-binds the auth headers, default headers and base URL exposed by
 * `ApiContext`. Use this from every query/mutation hook so we have a single
 * place to add cross-cutting concerns (retries, tracing, etc.) later.
 *
 * Returns the unwrapped JSON body — callers access `.data.entity` per the
 * API's envelope shape, no need to mess with `isEmpty()` themselves.
 *
 * Throws when the upstream returned an empty payload so TanStack Query's
 * error path kicks in.
 */
export const useApiClient = (): ApiClient => {
  const { apiUrl, getAuthHeaders, getDefaultHeaders } = useApi();

  return useMemo<ApiClient>(() => {
    const request = async <TResponse, TBody>(
      method: HttpMethod,
      path: string,
      body?: TBody,
      options?: ApiClientRequestOptions,
    ): Promise<TResponse> => {
      const authHeaders = await getAuthHeaders();
      const headers: Record<string, string> = {
        ...authHeaders,
        ...getDefaultHeaders(options?.buildingUuid),
        ...options?.headers,
      };

      const response = await apiRequest<TResponse, TBody, void>({
        url: composeUrl(apiUrl, path),
        method,
        headers,
        ...(body !== undefined && { body }),
        ...(options?.signal && { signal: options.signal }),
      });

      if (response.isEmpty()) {
        throw new Error(`Empty response from ${method} ${path}`);
      }

      return response.data;
    };

    return {
      get: <TResponse>(path: string, options?: ApiClientRequestOptions) =>
        request<TResponse, undefined>(HttpMethod.GET, path, undefined, options),
      post: <TResponse, TBody = unknown>(
        path: string,
        body?: TBody,
        options?: ApiClientRequestOptions,
      ) => request<TResponse, TBody>(HttpMethod.POST, path, body, options),
      put: <TResponse, TBody = unknown>(
        path: string,
        body?: TBody,
        options?: ApiClientRequestOptions,
      ) => request<TResponse, TBody>(HttpMethod.PUT, path, body, options),
      patch: <TResponse, TBody = unknown>(
        path: string,
        body?: TBody,
        options?: ApiClientRequestOptions,
      ) => request<TResponse, TBody>(HttpMethod.PATCH, path, body, options),
      delete: <TResponse>(path: string, options?: ApiClientRequestOptions) =>
        request<TResponse, undefined>(HttpMethod.DELETE, path, undefined, options),
    };
  }, [apiUrl, getAuthHeaders, getDefaultHeaders]);
};
